import React from 'react';
import { isEqual, get } from 'lodash';
import { FormattedMessage } from 'react-intl';
import resourcesConfig from '../resources';
import { formatDate } from '../utils/qwikyLocalizationHelper';
import orderingRoutes from '../routes/ordering';
import bookingRoutes from '../routes/booking';
import asyncRoute from '../helpers/hoc/asyncRoute';
import { prepareCouponWeekDays } from '../selectors';
import homeIcon from '../assets/icons/home.svg';

const Icons = asyncRoute(() => import('../components/Icons'));

export const navigationalPanelRoutes = [
  bookingRoutes.containers.home,
  bookingRoutes.containers.bookings,
  bookingRoutes.containers.account,
];

export const datePickerSettings = {
  openTo: 'year',
  views: ['year', 'month', 'day'],
  toolbarFormat: 'DD MMM YYYY',
};

export const orderTypes = {
  dineIn: 'DI',
  selfPickup: 'SP',
  delivery: 'DL',
};

export const rewardSignUpStates = {
  getStarted: 'GET_STARTED',
  enterNumber: 'ENTER_NUMBER',
  verifyNumber: 'VERIFY_NUMBER',
  enterDetails: 'ENTER_DETAILS',
  selectOutlet: 'SELECT_OUTLET',
};

export const rewardSigninStates = {
  enterNumber: 'ENTER_NUMBER',
  verifyNumber: 'VERIFY_NUMBER',
};

export const pointUnits = {
  percent: 'PER',
  absolute: 'ABS',
  item: 'ITM',
};

export const currencies = {
  INR: { text: 'Rs.', symbol: '₹' },
};

export const announcementBannerCarouselSettings = {
  dots: true,
  arrows: false,
  infinite: true,
  speed: 1000,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3000,
  lazyload: 'progressive', // accepts 'onDemand | progressive'
  variableWidth: true,
};

export const rewardCardsCarouselSettings = {
  dots: true,
  arrows: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false,
  autoplaySpeed: 3000,
  lazyload: 'progressive', // accepts 'onDemand | progressive'
};

export const couponCardsCarouselSettings = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false,
  autoplaySpeed: 3000,
  lazyload: 'progressive', // accepts 'onDemand | progressive'
  variableWidth: true,
};

export const outletImagesCarouselSettings = {
  dots: true,
  arrows: false,
  infinite: true,
  speed: 1000,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false,
  lazyload: 'progressive', // accepts 'onDemand | progressive'
};

export const rewardSettings = {
  home: 'HOME',
  profile: 'PROFILE',
  rewardProgram: 'REWARDS_PROGRAM',
  settings: 'SETTINGS',
  helpSupport: 'HELP_SUPPORT',
  vipPass: 'VIP_PASS',
  myRewards: 'MY_REWARDS',
  addressBook: 'ADDRESS_BOOK',
  policies: 'POLICIES',
  myBookings: 'MY_BOOKINGS',
  deleteAccount: 'DELETE_ACCOUNT',
};

export const minAgeYearsForRewardOnboarding = 8;

export const rewardTransactionCategory = {
  credit: 'C',
  debit: 'D',
};

export const rewardSourceType = {
  order: 'ORDER',
  coupon: 'COUPON',
};

export const rewardTransactionType = {
  reward: 'REW',
  redeem: 'RED',
  payment: 'PYMT',
  default: 'DF',
};

export const accountHistoryView = {
  point: 'point-history',
  reward: 'reward-history',
  order: 'order-history',
};

export const rewardType = {
  added: 'Added',
  deducted: 'Deducted',
  adjusted: 'Adjusted',
};
const { BIRTHDAY_REWARD_PRE_MONTHS, ANNIVERSARY_REWARD_PRE_MONTHS } =
  '_env' in window && window._env;

export const birthdayRewardPreMonths = BIRTHDAY_REWARD_PRE_MONTHS;

export const anniversaryrewardPreMonths = ANNIVERSARY_REWARD_PRE_MONTHS;

export const dateFilterConfig = {
  filters: {
    default: { code: 'DF', title: 'All time' },
    custom: { code: 'C', title: 'Custom' },
    thisWeek: { code: 'TW', title: 'This week' },
    thisMonth: { code: 'TM', title: 'This month' },
    lastWeek: { code: 'LW', title: 'Last week' },
    lastMonth: { code: 'LM', title: 'Last month' },
  },

  filterDateFormat: 'DD MMM YYYY',
};

export const rewardPointFilter = [
  { title: 'All', code: 'ALL' },
  { title: 'Earned', code: 'EARNED' },
  { title: 'Used', code: 'USED' },
];

export const rewardFilter = [
  { title: 'All', code: 'ALL' },
  { title: 'Claimed', code: 'CLAIMED' },
  { title: 'Redeemed', code: 'REDEEMED' },
  { title: 'Expired', code: 'EXPIRED' },
];

export const rewardCategory = {
  bday: 'BDAY',
  rewardPoints: 'REWARD_COUPON',
  campaign: 'CM',
  reward: 'RW',
  welcome: 'WELCOME',
  pointOffer: 'REWARD_POINT',
  anniversary: 'ANNIVERSARY',
  tierUpgrade: 'TIER_UPGRADE',
};

export const pointPriceExcludeRewardCategories = [
  rewardCategory.bday,
  rewardCategory.welcome,
  rewardCategory.anniversary,
  rewardCategory.tierUpgrade,
  'FB',
];

export const nativeEventType = {
  appVersion: 'app-version',
  location: 'location',
  locationPermission: 'locationPermission',
  routeName: 'routeName',
  hardwareBackPress: 'hardwareBackPress',
  verifyPayment: 'verify-payment',
  appStartupInfo: 'appStartupInfo',
  nativeAppUpdateAvailable: 'nativeAppUpdateAvailable',
  disableKBAvoidingScroll: 'disable-kb-avoiding-scroll',
  disableKBAvoidingView: 'disable-kb-avoiding-view',
  navigateToPage: 'navigateToPage',
  brandGuestData: 'brandGuestData',
  resetState: 'resetState',
  userLocation: 'user-location',
  goBack: 'go-back',
  canGoBack: 'can-go-back',
  nativeBackPress: 'nativeBackPress',
  selectLocation: 'select-location',
  deliveryLocation: 'delivery-location',
  selectOrderOutlet: 'select-order-outlet',
  lastOrderData: 'last-order-data',
  exitPayEarn: 'exit-pay-earn',
  exitBookTable: 'exit-book-table',
  canExitModule: 'can-exit-module',
  currentLocation: 'current-location',
  viewCartData: 'view-cart-data',
  nativeRouteName: 'nativeRouteName',
  clearCartAction: 'clearCartAction',
  showOutletsPopup: 'show-outlets-popup',
  updateMenuCart: 'update-menu-cart',
  restaurantSelection: 'restaurant-selection',
  scrollToMenuItem: 'scroll-to-menu-item',
  sessionExpired: 'session-expired',
  sessionHandled: 'session-handled',
  openScanner: 'open-scanner',
  closeScanner: 'close-scanner',
  enterCode: 'enter-code',
  permissions: 'permissions',
  scannedCode: 'scanned-code',
  openLocationSettings: 'open-location-settings',
  authTokenUpdated: 'auth-token-updated',
};

export const deafultFooterConfiguration = [
  {
    title: 'Home',
    route: 'home',
    icon: <Icons type="home" />,
  },

  {
    title: 'Bookings',
    route: 'bookings',
    icon: <Icons type="bookings" />,
  },
  {
    title: 'Account',
    route: 'account',
    icon: <Icons type="account" />,
  },
];

export const rewardRules = {
  spend: 'SPEND',
  rewardCoupon: 'REWARD_COUPON',
};

export const appOrderingStatusCodes = {
  comingSoon: 'DEMO',
  active: 'ACTIVE',
};

export const rewardTypes = {
  rewardPoint: 'RP',
};

export const referralRewardTypes = {
  rewardPoint: 'REWARD_POINT',
  deal: 'COUPON',
};

export const brandModuleCodes = {
  appOrder: 'appOrder',
  reward: 'reward',
  pass: 'pass',
  payAndEarn: 'payAndEarn',
  reservation: 'reservation',
  feedback: 'feedback',
  referral: 'referral',
};

export const loyaltyCarouselSettings = {
  dots: true,
  arrows: false,
  infinite: true,
  speed: 1000,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 3000,
  lazyload: 'progressive', // accepts 'onDemand | progressive'
  variableWidth: true,
};

export const foodItemType = {
  veg: { name: 'VEGETARIAN', code: 'VF' },
  nonVeg: { name: 'NON_VEGETARIAN', code: 'DF' },
  egg: { name: 'EGG', code: '' },
  all: { name: 'ALL', code: 'ALL' },
};

export const foodItemTypeDropdownOptions = [
  { title: foodItemType.all.name, value: foodItemType.all.code },
  { title: foodItemType.veg.name, value: foodItemType.veg.code },
  { title: foodItemType.nonVeg.name, value: foodItemType.nonVeg.code },
];

export const extraDiscountCode = 'OUTLETDISCOUNT';

export const tagTypes = {
  pointCredit: 'point_credit',
};

export const validationErrorPriority = {
  dayValidation: 6,
  dateValidation: 4,
  timeValidation: 7,
  redeemValidation: 1,
  expireValidation: 2,
  pauseValidation: 3,
  outletValidation: 5,
  maxAllotmentValidation: 10,
  perGuestMaxValidation: 11,
  pointsValidation: 9,
  rewardValidation: 12,
  amountValidation: 8,
};

export const getHighestPriorityError = (errors) => {
  let highestPriority = Number.MAX_VALUE;
  let highestPriorityError = null;

  // Iterate over each key in the API response
  for (const [key, message] of Object.entries(errors)) {
    // Check if the current error key has a defined priority
    if (validationErrorPriority.hasOwnProperty(key)) {
      const currentPriority = validationErrorPriority[key];

      // Update the highest priority error if the current one has a higher priority
      if (currentPriority < highestPriority) {
        highestPriority = currentPriority;
        highestPriorityError = { key, message };
      }
    }
  }
  return highestPriorityError;
};

export const generateValidationErrorMessage = ({ errorMessageObj, dealCategory, weekdays }) => {
  let errorText = '';
  const preText = isEqual(dealCategory, 'RW') ? (
    <FormattedMessage id="rewardText" defaultMessage={resourcesConfig.rewardText} />
  ) : (
    <FormattedMessage id="offerText" defaultMessage={resourcesConfig.offerText} />
  );
  const errorMessageParams = get(errorMessageObj, 'message.additionalParam');
  switch (get(errorMessageObj, 'key')) {
    case 'dayValidation':
      let applicableWeekdays = get(errorMessageParams, 'applicableWeekdays', []);
      applicableWeekdays = prepareCouponWeekDays(applicableWeekdays, weekdays);
      errorText = (
        <FormattedMessage
          id="dayValidationError"
          defaultMessage={resourcesConfig.dayValidationError}
          values={{ preText, applicableWeekdays }}
        />
      );
      break;
    case 'timeValidation':
      const startTime = get(errorMessageParams, 'startTime', '');
      const endTime = get(errorMessageParams, 'endTime', '');
      errorText = (
        <FormattedMessage
          id="timeValidationError"
          defaultMessage={resourcesConfig.timeValidationError}
          values={{ preText, startTime, endTime }}
        />
      );
      break;
    case 'dateValidation':
      const activationTime = get(errorMessageParams, 'activationTime', '');
      const updatedActivationTime = activationTime && formatDate(activationTime);
      errorText = (
        <FormattedMessage
          id="dateValidationError"
          defaultMessage={resourcesConfig.dateValidationError}
          values={{ preText, activationTime: updatedActivationTime }}
        />
      );
      break;
    case 'amountValidation':
      const minOrderValue = get(errorMessageParams, 'minOrderValue', '');
      errorText = (
        <FormattedMessage
          id="amountValidationError"
          defaultMessage={resourcesConfig.amountValidationError}
          values={{ preText, minOrderValue, currencySymbol: currencies.INR.symbol }}
        />
      );
      break;
    case 'expireValidation':
      const expireDate = get(errorMessageParams, 'expireTime', '');
      const updatedExpireDate = expireDate && formatDate(expireDate);
      errorText = (
        <FormattedMessage
          id={expireDate ? 'expireValidationDateError' : 'expireValidationError'}
          defaultMessage={
            expireDate
              ? resourcesConfig.expireValidationDateError
              : resourcesConfig.expireValidationError
          }
          values={{ preText, expireDate: updatedExpireDate }}
        />
      );
      break;

    default:
      errorText = get(errorMessageParams, 'invalidOfferMsg') ? (
        get(errorMessageParams, 'invalidOfferMsg')
      ) : (
        <FormattedMessage
          id="offerNotApplicableDescription"
          defaultMessage={resourcesConfig.offerNotApplicableDescription}
        />
      );
      break;
  }
  return errorText;
};

export const generateValidationErrorTitle = ({ errorMessageObj, dealCategory }) => {
  let errorText = '';
  const preText = isEqual(dealCategory, 'RW') ? (
    <FormattedMessage id="rewardText" defaultMessage={resourcesConfig.rewardText} />
  ) : (
    <FormattedMessage id="offerText" defaultMessage={resourcesConfig.offerText} />
  );
  switch (get(errorMessageObj, 'key')) {
    case 'amountValidation':
      errorText = (
        <FormattedMessage
          id="amountValidationErrorTitle"
          defaultMessage={resourcesConfig.amountValidationErrorTitle}
        />
      );
      break;
    case 'expireValidation':
      errorText = (
        <FormattedMessage
          id="expireValidationErrorTitle"
          defaultMessage={resourcesConfig.expireValidationErrorTitle}
        />
      );
      break;

    default:
      errorText = (
        <FormattedMessage
          id="offerNotApplicableTitle"
          defaultMessage={resourcesConfig.offerNotApplicableTitle}
          values={{ preText }}
        />
      );
      break;
  }
  return errorText;
};
